// Steps/SelectNumberStep.jsx
// Country is fixed to GB. Resolves the TWILIO CallProvider_Id from
// /callprovider/list, then searches /telephonenumberaccount/available with an
// optional area code / contains filter and renders the available_list as
// selectable cards. Stores the chosen phone_number on the wizard bag.

import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { Label, Input, FormGroup, Card, Spinner } from "reactstrap";
import { useSelector } from "react-redux";
import { isEmpty } from "lodash";
import { toast } from "react-toastify";

import Select from "../../../Common/AppSelect";
import { countryOptions } from "../../../../common/data/countries";
import {
  callProviderList,
  telephoneNumberAvailable,
} from "../../../../store/thirdPartyIntegration/action";

export function SelectNumberStep({ wizard }) {
  const {
    dispatch,
    callProviderId,
    setCallProviderId,
    country,
    setCountry,
    selectedNumber,
    setSelectedNumber,
    areaCode,
    setAreaCode,
    contains,
    setContains,
    numberType,
    setNumberType,
    goNext,
    goBack,
  } = wizard;

  const {
    callProviderListDataAPI,
    availableDataAPI,
    availableLoading,
    providerLoading,
  } = useSelector((state) => ({
    callProviderListDataAPI:
      state.ThirdPartyIntegration.callProviderListData,
    availableDataAPI: state.ThirdPartyIntegration.telephoneNumberAvailableData,
    availableLoading:
      state.ThirdPartyIntegration.telephoneNumberAvailableLoading,
    providerLoading: state.ThirdPartyIntegration.callProviderListLoading,
  }));

  // Resolve the TWILIO provider on mount (once).
  useEffect(() => {
    dispatch(callProviderList());
  }, []);

  useEffect(() => {
    if (isEmpty(callProviderListDataAPI)) return;
    const providers = callProviderListDataAPI?.callprovider_list || [];
    const twilio = providers.find((p) => p.Code === "TWILIO");
    if (twilio?.Id) {
      setCallProviderId(twilio.Id);
    }
  }, [callProviderListDataAPI]);

  const availableList = availableDataAPI?.available_list || [];

  // Why the Search button might be disabled. The provider list has to load and
  // contain a TWILIO row before we have a CallProvider_Id to search with; until
  // then the button is disabled. Derived straight from the API data (not the
  // callProviderId state) so there's no flash of "missing" on the render before
  // the resolver effect runs.
  const providersLoaded = !isEmpty(callProviderListDataAPI);
  const twilioResolved = (
    callProviderListDataAPI?.callprovider_list || []
  ).some((p) => p.Code === "TWILIO");
  const twilioMissing = providersLoaded && !providerLoading && !twilioResolved;
  const searchDisabled =
    availableLoading || providerLoading || !callProviderId;

  const handleSearch = () => {
    if (!callProviderId) {
      toast.error("Phone numbers are not set up for this company yet");
      return;
    }
    dispatch(
      telephoneNumberAvailable({
        CallProvider_Id: callProviderId,
        CountryCode: country || "GB",
        Contains: contains || undefined,
        AreaCode: numberType === "mobile" ? undefined : areaCode || undefined,
        // Change #100774 - only sent for mobile, so an older backend sees the
        // same request as before for a landline search.
        ...(numberType === "mobile" ? { NumberType: "mobile" } : {}),
      })
    );
  };

  const canAdvance = !!selectedNumber;

  return (
    <div className="step-container">
      <div className="mb-4 pb-2 border-bottom">
        <h4 className="text-pastel-blue fw-bold mb-1">Choose a phone number</h4>
        <p className="text-muted small mb-0">
          Pick a country, then search available numbers to buy.
        </p>
      </div>

      {/* Change #100774 - in the UK only mobile (07) numbers can text. */}
      <div className="btn-group mb-3" role="group" aria-label="Number type">
        <button
          type="button"
          className={`btn ${numberType === "mobile" ? "btn-primary" : "btn-outline-primary"}`}
          onClick={() => {
            setNumberType("mobile");
            setSelectedNumber("");
          }}
        >
          <i className="ri-message-2-line me-1"></i> Mobile - calls and texts
        </button>
        <button
          type="button"
          className={`btn ${numberType !== "mobile" ? "btn-primary" : "btn-outline-primary"}`}
          onClick={() => {
            setNumberType("local");
            setSelectedNumber("");
          }}
        >
          <i className="ri-phone-line me-1"></i> Landline - calls only
        </button>
      </div>
      {numberType === "mobile" && (
        <p className="text-muted small mb-3">
          Mobile numbers can send and receive texts. In the UK they need your business verified
          before one can be bought (Settings &gt; SMS &gt; Send From &amp; Replies).
        </p>
      )}

      <div className="row g-3 align-items-end mb-3">
        <div className="col-md-4">
          <FormGroup className="mb-0">
            <Label for="tel-country">Country</Label>
            <Select
              inputId="tel-country"
              options={countryOptions}
              value={
                countryOptions.find((o) => o.value === country) || null
              }
              onChange={(opt) => {
                setCountry(opt?.value || "GB");
                // A number chosen for one country isn't valid for another —
                // clear the selection when the country changes.
                setSelectedNumber("");
              }}
              placeholder="Select country"
              isSearchable
            />
          </FormGroup>
        </div>
        <div className="col-md-3">
          <FormGroup className="mb-0">
            <Label for="tel-area-code">Area code</Label>
            <Input
              id="tel-area-code"
              type="text"
              value={numberType === "mobile" ? "" : areaCode}
              placeholder={numberType === "mobile" ? "n/a for mobiles" : "e.g. 117"}
              disabled={numberType === "mobile"}
              onChange={(e) => setAreaCode(e.target.value)}
            />
          </FormGroup>
        </div>
        <div className="col-md-3">
          <FormGroup className="mb-0">
            <Label for="tel-contains">Contains</Label>
            <Input
              id="tel-contains"
              type="text"
              value={contains}
              placeholder="digits"
              onChange={(e) => setContains(e.target.value)}
            />
          </FormGroup>
        </div>
        <div className="col-md-2">
          <button
            type="button"
            className="btn btn-primary w-100"
            onClick={handleSearch}
            disabled={searchDisabled}
            title={
              providerLoading
                ? "Loading telephony provider…"
                : twilioMissing
                ? "Phone numbers aren't set up for this company yet"
                : undefined
            }
          >
            {providerLoading || availableLoading ? (
              <Spinner size="sm" />
            ) : (
              "Search"
            )}
          </button>
        </div>
      </div>

      {/* Explain why Search is disabled instead of leaving it dead with no
          feedback (the "search box is disabled and can't understand why" bug). */}
      {twilioMissing && (
        <div className="alert alert-warning d-flex align-items-center mb-3">
          <i className="ri-error-warning-line fs-5 me-2"></i>
          <span>
            Phone numbers aren't set up for this company yet, so numbers can't be
            searched. Once they are set up, this search will
            enable automatically.
          </span>
        </div>
      )}

      {/* Results */}
      {availableLoading ? (
        <div className="text-center text-muted py-4">
          <Spinner size="sm" className="me-2" /> Searching numbers…
        </div>
      ) : availableList.length > 0 ? (
        <div className="d-flex flex-column gap-2">
          {availableList.map((item) => {
            const phone = item.phone_number;
            const active = selectedNumber === phone;
            return (
              <Card
                key={phone}
                className={`p-3 border rounded-3 cursor-pointer ${
                  active
                    ? "border-pastel-blue bg-pastel-blue shadow-sm"
                    : "border-light bg-white"
                }`}
                role="button"
                onClick={() => setSelectedNumber(phone)}
              >
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center gap-3">
                    <Input
                      type="radio"
                      className="m-0"
                      checked={active}
                      onChange={() => setSelectedNumber(phone)}
                    />
                    <div>
                      <div className="fw-bold text-dark">{phone}</div>
                      {item.friendly_name && (
                        <small className="text-muted">
                          {item.friendly_name}
                        </small>
                      )}
                    </div>
                  </div>
                  <i className="ri-phone-line fs-4 text-muted"></i>
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
        <div className="text-muted fst-italic small py-3">
          {availableDataAPI && !isEmpty(availableDataAPI)
            ? "No numbers matched. Try a different area code or filter."
            : "Run a search to see available numbers."}
        </div>
      )}

      <div className="d-flex align-items-center justify-content-between mt-4 pt-3 border-top">
        <button
          type="button"
          className="btn btn-light rounded-pill px-4 fw-semibold text-muted"
          onClick={goBack}
        >
          <i className="ri-arrow-left-line me-2"></i> Back
        </button>
        <button
          type="button"
          className="btn btn-primary rounded-pill px-4 fw-semibold"
          onClick={goNext}
          disabled={!canAdvance}
        >
          Next <i className="ri-arrow-right-line ms-2"></i>
        </button>
      </div>
    </div>
  );
}

SelectNumberStep.propTypes = {
  wizard: PropTypes.object.isRequired,
};
